import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Trophy, RotateCcw, Share2, Home, Play, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SqueegeeGamePage = () => {
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState('start'); // start, playing, finished
  const [timer, setTimer] = useState(0);
  const [cleanPercentage, setCleanPercentage] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const [squeegeePos, setSqueegeePos] = useState({ x: 175, y: 250 });
  const [paths, setPaths] = useState([]);
  const [currentPath, setCurrentPath] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [playerName, setPlayerName] = useState('');
  const [showNameInput, setShowNameInput] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [useLocalStorage, setUseLocalStorage] = useState(false);
  const timerRef = useRef(null);
  const dirtyPixelsRef = useRef(null);
  const cleanedPixelsRef = useRef(new Set());

  const SQUEEGEE_WIDTH = 140;
  const SQUEEGEE_HEIGHT = 40;
  const CANVAS_WIDTH = 350;
  const CANVAS_HEIGHT = 500;
  const GRID_SIZE = 10; // Track cleaning in 10x10 blocks instead of per-pixel

  // Helper to filter entries older than 30 days
  const filterValidEntries = (entries) => {
    const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
    return entries.filter(entry => {
      const entryTime = entry.timestamp || 0;
      return entryTime > thirtyDaysAgo;
    });
  };

  // Load leaderboard - try API first, fall back to localStorage
  useEffect(() => {
    const fetchLeaderboard = async () => {
      // Try API first
      if (API_URL) {
        try {
          const response = await fetch(`${API_URL}/api/leaderboard`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
          });
          if (response.ok) {
            const data = await response.json();
            setLeaderboard(data);
            return; // API works, we're done
          }
        } catch (error) {
          console.log('API not available, using localStorage');
        }
      }
      
      // Fall back to localStorage
      setUseLocalStorage(true);
      const saved = localStorage.getItem('squeegeeLeaderboard');
      if (saved) {
        try {
          const entries = JSON.parse(saved);
          const validEntries = filterValidEntries(entries);
          localStorage.setItem('squeegeeLeaderboard', JSON.stringify(validEntries));
          setLeaderboard(validEntries);
        } catch (e) {
          console.error('Error parsing localStorage:', e);
        }
      }
    };
    fetchLeaderboard();
  }, []);

  // Draw clean shower background with tiles and shower head
  const drawCleanBackground = useCallback((ctx, x, y, width, height) => {
    // White/light gray tile background
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(x, y, width, height);
    
    // Draw tile grid
    ctx.strokeStyle = '#e0e0e0';
    ctx.lineWidth = 2;
    
    // Vertical tile lines
    for (let tx = 0; tx < CANVAS_WIDTH; tx += 60) {
      if (tx >= x && tx <= x + width) {
        ctx.beginPath();
        ctx.moveTo(tx, Math.max(0, y));
        ctx.lineTo(tx, Math.min(CANVAS_HEIGHT, y + height));
        ctx.stroke();
      }
    }
    
    // Horizontal tile lines
    for (let ty = 0; ty < CANVAS_HEIGHT; ty += 60) {
      if (ty >= y && ty <= y + height) {
        ctx.beginPath();
        ctx.moveTo(Math.max(0, x), ty);
        ctx.lineTo(Math.min(CANVAS_WIDTH, x + width), ty);
        ctx.stroke();
      }
    }
  }, []);

  // Draw the shower head (called once when game starts)
  const drawShowerHead = useCallback((ctx) => {
    // Shower arm coming from wall
    ctx.fillStyle = '#c0c0c0';
    ctx.fillRect(CANVAS_WIDTH - 60, 0, 20, 60);
    
    // Shower head mount
    ctx.beginPath();
    ctx.arc(CANVAS_WIDTH - 50, 70, 15, 0, Math.PI * 2);
    ctx.fillStyle = '#b0b0b0';
    ctx.fill();
    ctx.strokeStyle = '#909090';
    ctx.lineWidth = 2;
    ctx.stroke();
    
    // Shower head
    ctx.beginPath();
    ctx.ellipse(CANVAS_WIDTH - 50, 100, 35, 20, 0, 0, Math.PI * 2);
    ctx.fillStyle = 'linear-gradient(#d0d0d0, #a0a0a0)';
    ctx.fillStyle = '#c8c8c8';
    ctx.fill();
    ctx.strokeStyle = '#909090';
    ctx.lineWidth = 2;
    ctx.stroke();
    
    // Shower head face plate
    ctx.beginPath();
    ctx.ellipse(CANVAS_WIDTH - 50, 105, 30, 15, 0, 0, Math.PI * 2);
    ctx.fillStyle = '#e8e8e8';
    ctx.fill();
    ctx.strokeStyle = '#b0b0b0';
    ctx.stroke();
    
    // Water holes on shower head
    ctx.fillStyle = '#a0a0a0';
    for (let i = 0; i < 5; i++) {
      for (let j = 0; j < 3; j++) {
        ctx.beginPath();
        ctx.arc(CANVAS_WIDTH - 65 + i * 8, 100 + j * 6, 2, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }, []);

  // Initialize dirty glass pattern
  const initDirtyGlass = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    // First draw the clean background (will be revealed when squeegee cleans)
    drawCleanBackground(ctx, 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    
    // Draw the shower head
    drawShowerHead(ctx);
    
    // Save the clean state
    const cleanState = ctx.getImageData(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    
    // Now draw steam/fog overlay on top
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    
    // Add water droplets/bubbles
    for (let i = 0; i < 300; i++) {
      const x = Math.random() * CANVAS_WIDTH;
      const y = Math.random() * CANVAS_HEIGHT;
      const radius = Math.random() * 10 + 3;
      
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(180, 210, 230, ${Math.random() * 0.5 + 0.3})`;
      ctx.fill();
      
      // Add highlight to bubbles for 3D effect
      ctx.beginPath();
      ctx.arc(x - radius * 0.3, y - radius * 0.3, radius * 0.3, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255, 255, 255, ${Math.random() * 0.4 + 0.2})`;
      ctx.fill();
    }
    
    // Store the clean background for revealing
    dirtyPixelsRef.current = cleanState;
    cleanedPixelsRef.current = new Set();
  }, [drawCleanBackground, drawShowerHead]);

  // Start game
  const startGame = () => {
    setGameState('playing');
    setTimer(0);
    setCleanPercentage(0);
    setPaths([]);
    setCurrentPath([]);
    cleanedPixelsRef.current = new Set();
    
    initDirtyGlass();
    
    timerRef.current = setInterval(() => {
      setTimer(t => t + 10);
    }, 10);
  };

  // Calculate clean percentage using grid blocks
  const calculateCleanPercentage = useCallback(() => {
    const gridCols = Math.ceil(CANVAS_WIDTH / GRID_SIZE);
    const gridRows = Math.ceil(CANVAS_HEIGHT / GRID_SIZE);
    const totalBlocks = gridCols * gridRows;
    const cleanedCount = cleanedPixelsRef.current.size;
    const percentage = Math.min(100, (cleanedCount / (totalBlocks * 0.85)) * 100);
    return Math.floor(percentage);
  }, []);

  // Draw squeegee effect - completely clear the area
  const drawSqueegee = useCallback((x, y) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    // Update squeegee position
    setSqueegeePos({ x, y });
    
    // First, completely clear the squeegee area
    ctx.clearRect(x - SQUEEGEE_WIDTH/2, y - SQUEEGEE_HEIGHT/2, SQUEEGEE_WIDTH, SQUEEGEE_HEIGHT);
    
    // Draw clean white tile background in that area
    ctx.save();
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(x - SQUEEGEE_WIDTH/2, y - SQUEEGEE_HEIGHT/2, SQUEEGEE_WIDTH, SQUEEGEE_HEIGHT);
    
    // Draw tile lines in cleaned area
    ctx.strokeStyle = '#e0e0e0';
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let tx = 0; tx < CANVAS_WIDTH; tx += 60) {
      if (tx >= x - SQUEEGEE_WIDTH/2 && tx <= x + SQUEEGEE_WIDTH/2) {
        ctx.moveTo(tx, Math.max(0, y - SQUEEGEE_HEIGHT/2));
        ctx.lineTo(tx, Math.min(CANVAS_HEIGHT, y + SQUEEGEE_HEIGHT/2));
      }
    }
    for (let ty = 0; ty < CANVAS_HEIGHT; ty += 60) {
      if (ty >= y - SQUEEGEE_HEIGHT/2 && ty <= y + SQUEEGEE_HEIGHT/2) {
        ctx.moveTo(Math.max(0, x - SQUEEGEE_WIDTH/2), ty);
        ctx.lineTo(Math.min(CANVAS_WIDTH, x + SQUEEGEE_WIDTH/2), ty);
      }
    }
    ctx.stroke();
    
    // Redraw shower head if we're cleaning in that area
    if (x + SQUEEGEE_WIDTH/2 > CANVAS_WIDTH - 90 && y - SQUEEGEE_HEIGHT/2 < 130) {
      // Shower arm
      ctx.fillStyle = '#c0c0c0';
      ctx.fillRect(CANVAS_WIDTH - 60, 0, 20, 60);
      
      // Shower head mount
      ctx.beginPath();
      ctx.arc(CANVAS_WIDTH - 50, 70, 15, 0, Math.PI * 2);
      ctx.fillStyle = '#b0b0b0';
      ctx.fill();
      ctx.strokeStyle = '#909090';
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Shower head
      ctx.beginPath();
      ctx.ellipse(CANVAS_WIDTH - 50, 100, 35, 20, 0, 0, Math.PI * 2);
      ctx.fillStyle = '#c8c8c8';
      ctx.fill();
      ctx.strokeStyle = '#909090';
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Shower head face plate
      ctx.beginPath();
      ctx.ellipse(CANVAS_WIDTH - 50, 105, 30, 15, 0, 0, Math.PI * 2);
      ctx.fillStyle = '#e8e8e8';
      ctx.fill();
      ctx.strokeStyle = '#b0b0b0';
      ctx.stroke();
      
      // Water holes
      ctx.fillStyle = '#a0a0a0';
      for (let i = 0; i < 5; i++) {
        for (let j = 0; j < 3; j++) {
          ctx.beginPath();
          ctx.arc(CANVAS_WIDTH - 65 + i * 8, 100 + j * 6, 2, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
    ctx.restore();
    
    // Track cleaned grid blocks (much faster than per-pixel)
    const startCol = Math.floor(Math.max(0, x - SQUEEGEE_WIDTH/2) / GRID_SIZE);
    const endCol = Math.floor(Math.min(CANVAS_WIDTH - 1, x + SQUEEGEE_WIDTH/2) / GRID_SIZE);
    const startRow = Math.floor(Math.max(0, y - SQUEEGEE_HEIGHT/2) / GRID_SIZE);
    const endRow = Math.floor(Math.min(CANVAS_HEIGHT - 1, y + SQUEEGEE_HEIGHT/2) / GRID_SIZE);
    for (let col = startCol; col <= endCol; col++) {
      for (let row = startRow; row <= endRow; row++) {
        cleanedPixelsRef.current.add(`${col},${row}`);
      }
    }
    
    const newPercentage = calculateCleanPercentage();
    setCleanPercentage(newPercentage);
    
    // Check win condition
    if (newPercentage >= 100) {
      endGame();
    }
  }, [calculateCleanPercentage]);

  // Handle mouse/touch events
  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const handleStart = (e) => {
    if (gameState !== 'playing') return;
    e.preventDefault();
    setIsDrawing(true);
    const coords = getCoordinates(e);
    setCurrentPath([coords]);
    drawSqueegee(coords.x, coords.y);
  };

  const handleMove = (e) => {
    if (gameState !== 'playing') return;
    e.preventDefault();
    const coords = getCoordinates(e);
    
    // Always update squeegee position for visual
    setSqueegeePos(coords);
    
    // Only draw/clean if actively pressing
    if (isDrawing) {
      setCurrentPath(prev => [...prev, coords]);
      drawSqueegee(coords.x, coords.y);
    }
  };

  const handleEnd = () => {
    if (currentPath.length > 0) {
      setPaths(prev => [...prev, currentPath]);
    }
    setIsDrawing(false);
    setCurrentPath([]);
  };

  // End game
  const endGame = () => {
    clearInterval(timerRef.current);
    setGameState('finished');
    setShowNameInput(true);
  };

  // Get rating based on time
  const getRating = () => {
    const seconds = timer / 1000;
    if (seconds < 10) return { title: "Squeegee Savant! 🏆", stars: 5 };
    if (seconds < 15) return { title: "Clear Visionary! ✨", stars: 4 };
    if (seconds < 20) return { title: "Glass Master! 💎", stars: 3 };
    if (seconds < 30) return { title: "Streak-Free Pro! 👍", stars: 2 };
    return { title: "Getting Clearer! 🌟", stars: 1 };
  };

  // Save to leaderboard - try API first, fall back to localStorage
  const saveScore = async () => {
    if (!playerName.trim()) return;
    if (isSaving) return;
    
    setIsSaving(true);
    
    const newEntry = {
      id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
      name: playerName.trim(),
      time: timer,
      date: new Date().toLocaleDateString(),
      timestamp: Date.now()
    };
    
    // Try API first if not already using localStorage
    if (!useLocalStorage && API_URL) {
      try {
        const response = await fetch(`${API_URL}/api/leaderboard`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: playerName.trim(),
            time: timer
          })
        });
        
        if (response.ok) {
          // Refresh leaderboard from API
          const leaderboardResponse = await fetch(`${API_URL}/api/leaderboard`);
          if (leaderboardResponse.ok) {
            const data = await leaderboardResponse.json();
            setLeaderboard(data);
          }
          setShowNameInput(false);
          setIsSaving(false);
          return;
        }
      } catch (error) {
        console.log('API save failed, using localStorage');
        setUseLocalStorage(true);
      }
    }
    
    // Fall back to localStorage
    const validEntries = filterValidEntries(leaderboard);
    const newLeaderboard = [...validEntries, newEntry]
      .sort((a, b) => a.time - b.time)
      .slice(0, 10);
    
    setLeaderboard(newLeaderboard);
    localStorage.setItem('squeegeeLeaderboard', JSON.stringify(newLeaderboard));
    setShowNameInput(false);
    setIsSaving(false);
  };

  // Format time
  const formatTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const milliseconds = Math.floor((ms % 1000) / 10);
    return `${seconds}.${milliseconds.toString().padStart(2, '0')}s`;
  };

  // Share result
  const shareResult = () => {
    const rating = getRating();
    const text = `I cleaned a shower door in ${formatTime(timer)} and earned "${rating.title}" in The Perfect Squeegee game! Can you beat my time? 🚿✨`;
    
    if (navigator.share) {
      navigator.share({
        title: 'The Perfect Squeegee',
        text: text,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(text + ' ' + window.location.href);
      alert('Result copied to clipboard!');
    }
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-blue-100">
      <div className="container-custom py-8">
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="text-center mb-6">
            <Badge className="mb-2 bg-primary/20 text-primary border-primary/30">Mini Game</Badge>
            <h1 className="text-3xl font-bold mb-2">
              The Perfect <span className="text-gradient-cyan">Squeegee</span>
            </h1>
            <p className="text-muted-foreground text-sm">
              Clean the steamy glass as fast as you can!
            </p>
          </div>

          {/* Game Area */}
          <Card className="border-2 border-primary/20 overflow-hidden mb-4">
            <CardContent className="p-0 relative">
              {/* Start Screen */}
              {gameState === 'start' && (
                <div className="absolute inset-0 bg-gradient-to-b from-white/95 to-cyan-50/95 z-10 flex flex-col items-center justify-center p-6">
                  <Sparkles className="w-16 h-16 text-primary mb-4" />
                  <h2 className="text-2xl font-bold mb-2">Ready to Clean?</h2>
                  <p className="text-muted-foreground text-center mb-6 text-sm">
                    Swipe across the steamy glass to squeegee it clean. Get 100% coverage as fast as possible!
                  </p>
                  <Button size="lg" onClick={startGame} className="bg-primary hover:bg-primary/90">
                    <Play className="mr-2 h-5 w-5" />
                    Start Game
                  </Button>
                </div>
              )}

              {/* Finished Screen */}
              {gameState === 'finished' && (
                <div className="absolute inset-0 bg-gradient-to-b from-white/95 to-green-50/95 z-10 flex flex-col items-center justify-center p-6">
                  <div className="text-center">
                    <div className="text-5xl mb-2">
                      {[...Array(getRating().stars)].map((_, i) => (
                        <span key={i}>⭐</span>
                      ))}
                    </div>
                    <h2 className="text-2xl font-bold mb-1">{getRating().title}</h2>
                    <p className="text-4xl font-bold text-primary mb-4">{formatTime(timer)}</p>
                    
                    {showNameInput ? (
                      <div className="mb-4">
                        <input
                          type="text"
                          placeholder="Enter your name"
                          value={playerName}
                          onChange={(e) => setPlayerName(e.target.value)}
                          className="w-full px-4 py-2 border rounded-lg mb-2 text-center"
                          maxLength={15}
                          disabled={isSaving}
                        />
                        <Button 
                          onClick={saveScore} 
                          className="w-full bg-primary"
                          disabled={isSaving || !playerName.trim()}
                        >
                          {isSaving ? 'Saving...' : 'Save to Leaderboard'}
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-2">
                        <Button onClick={shareResult} variant="outline" className="w-full">
                          <Share2 className="mr-2 h-4 w-4" />
                          Share Result
                        </Button>
                        <Button onClick={startGame} className="w-full bg-primary">
                          <RotateCcw className="mr-2 h-4 w-4" />
                          Play Again
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Game Stats */}
              {gameState === 'playing' && (
                <div className="absolute top-2 left-2 right-2 z-10 flex justify-between">
                  <Badge variant="secondary" className="bg-white/90 text-lg font-mono">
                    {formatTime(timer)}
                  </Badge>
                  <Badge variant="secondary" className="bg-white/90 text-lg">
                    {cleanPercentage}% Clean
                  </Badge>
                </div>
              )}

              {/* Squeegee Cursor */}
              {gameState === 'playing' && (
                <div 
                  className="absolute pointer-events-none z-20 transition-transform duration-75"
                  style={{
                    left: `${(squeegeePos.x / CANVAS_WIDTH) * 100}%`,
                    top: `${(squeegeePos.y / CANVAS_HEIGHT) * 100}%`,
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  {/* Squeegee Handle */}
                  <div className="relative">
                    <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-4 h-12 bg-gradient-to-b from-gray-600 to-gray-800 rounded-t-full" />
                    {/* Squeegee Blade */}
                    <div className="w-36 h-6 bg-gradient-to-b from-cyan-400 to-cyan-600 rounded-sm shadow-lg border-b-2 border-cyan-700">
                      <div className="w-full h-2 bg-white/30 rounded-t-sm" />
                    </div>
                    {/* Rubber Edge */}
                    <div className="w-36 h-1.5 bg-gray-800 rounded-b-sm" />
                  </div>
                </div>
              )}

              {/* Canvas */}
              <canvas
                ref={canvasRef}
                width={CANVAS_WIDTH}
                height={CANVAS_HEIGHT}
                className="w-full touch-none cursor-pointer"
                style={{ maxWidth: '350px', margin: '0 auto', display: 'block' }}
                onMouseDown={handleStart}
                onMouseMove={handleMove}
                onMouseUp={handleEnd}
                onMouseLeave={handleEnd}
                onTouchStart={handleStart}
                onTouchMove={handleMove}
                onTouchEnd={handleEnd}
              />
            </CardContent>
          </Card>

          {/* Leaderboard */}
          {leaderboard.length > 0 && (
            <Card className="mb-4">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Trophy className="h-5 w-5 text-yellow-500" />
                  <h3 className="font-bold">Leaderboard</h3>
                </div>
                <div className="space-y-2">
                  {leaderboard.slice(0, 10).map((entry, index) => (
                    <div key={index} className="flex justify-between items-center text-sm">
                      <span className="flex items-center gap-2">
                        <span className={`font-bold ${index === 0 ? 'text-yellow-500' : index === 1 ? 'text-gray-400' : index === 2 ? 'text-amber-600' : ''}`}>
                          #{index + 1}
                        </span>
                        {entry.name}
                      </span>
                      <span className="font-mono text-primary">{formatTime(entry.time)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Back Link */}
          <div className="text-center">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/">
                <Home className="mr-2 h-4 w-4" />
                Back to Comal Glass
              </Link>
            </Button>
          </div>

          {/* Promo */}
          <div className="mt-6 text-center">
            <p className="text-xs text-muted-foreground mb-2">
              Want glass this easy to clean in real life?
            </p>
            <Button variant="outline" size="sm" asChild>
              <Link to="/shower-glass#showerguard">
                Learn About ShowerGuard
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SqueegeeGamePage;
